import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Wallet } from "lucide-react";
import SpendEntries from "./SpendEntries";

const API = process.env.REACT_APP_BACKEND_URL;
export const money = (v) =>
  v === null || v === undefined ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v);
const thisMonth = () => new Date().toISOString().slice(0, 7);
const shiftMonth = (m, d) => {
  const [y, mo] = m.split("-").map(Number);
  const t = new Date(Date.UTC(y, mo - 1 + d, 1));
  return t.toISOString().slice(0, 7);
};
const monthLabel = (m) =>
  new Date(`${m}-01T00:00:00Z`).toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
export const field = "w-full px-3 py-2 rounded-lg border border-[#D9DEE4] bg-white text-[14px] focus:outline-none focus:ring-2 focus:ring-[#334E68]";

function BudgetCell({ row, month, token, onSaved }) {
  const [value, setValue] = useState(row.budget ?? "");
  const [error, setError] = useState(false);
  useEffect(() => setValue(row.budget ?? ""), [row.budget]);
  const save = async () => {
    setError(false);
    const cleared = value === "";
    if ((cleared && row.budget === null) || (!cleared && Number(value) === row.budget)) return;
    if (!cleared && Number(value) < 0) return setError(true);
    const q = new URLSearchParams({ month, source: row.source, campaign: row.campaign });
    const r = await fetch(cleared ? `${API}/api/marketing/stats/budget?${q}` : `${API}/api/marketing/stats/budget`, {
      method: cleared ? "DELETE" : "PUT",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: cleared ? undefined : JSON.stringify({ month, source: row.source, campaign: row.campaign, budget: Number(value) }),
    }).catch(() => null);
    if (r?.ok) onSaved();
    else setError(true);
  };
  return (
    <input type="number" min="0" step="0.01" inputMode="decimal" value={value} placeholder="Set"
      onChange={(e) => setValue(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      aria-label={`Budget for ${row.source} ${row.campaign}`} aria-invalid={error} title={error ? "Could not save this budget" : "Clear the field to remove the budget"}
      data-testid={`budget-input-${row.source}-${row.campaign}`}
      className={`w-24 px-2 py-1.5 rounded-lg border text-right tabular-nums text-[14px] focus:outline-none focus:ring-2 focus:ring-[#334E68] ${error ? "border-[#B42318]" : "border-[#D9DEE4]"}`} />
  );
}

function Row({ r, month, token, onSaved, total }) {
  const td = "px-4 py-3 text-right tabular-nums whitespace-nowrap";
  const red = r.over_budget ? "text-[#B42318] font-semibold" : "";
  return (
    <tr className={`border-t border-[#EEE9E0] ${total ? "bg-[#FAF7F2] font-semibold" : ""}`}
      data-testid={total ? "budget-total-row" : `budget-row-${r.source}-${r.campaign}`}>
      <td className="px-4 py-3 whitespace-nowrap">{total ? "All campaigns" : <><span className="font-medium">{r.source}</span> · {r.campaign}</>}</td>
      <td className={td}>{total ? money(r.budget) : <BudgetCell row={r} month={month} token={token} onSaved={onSaved} />}</td>
      <td className={`${td} ${red}`}>{money(r.spent)}</td>
      <td className={`${td} ${red}`} data-testid={total ? "budget-total-remaining" : undefined}>{money(r.remaining)}</td>
      <td className={td}>{r.visit}</td>
      <td className={td}>{r.get_started}</td>
      <td className={td}>{r.app_store}</td>
      <td className={td}>{money(r.cost_per_click)}</td>
    </tr>
  );
}

function AddBudget({ month, token, onSaved }) {
  const [source, setSource] = useState("");
  const [campaign, setCampaign] = useState("");
  const [budget, setBudget] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const r = await fetch(`${API}/api/marketing/stats/budget`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ month, source, campaign, budget: Number(budget) }),
    }).catch(() => null);
    if (!r?.ok) return setError("Could not save the budget. Check the fields and try again.");
    setSource(""); setCampaign(""); setBudget("");
    onSaved();
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end" data-testid="add-budget-form">
      <input className={field} placeholder="Source (e.g. facebook)" value={source} onChange={(e) => setSource(e.target.value)} required aria-label="Source" data-testid="add-budget-source" />
      <input className={field} placeholder="Campaign (e.g. launch-week)" value={campaign} onChange={(e) => setCampaign(e.target.value)} required aria-label="Campaign" data-testid="add-budget-campaign" />
      <input className={field} type="number" min="0" step="0.01" inputMode="decimal" placeholder="Monthly budget ($)" value={budget} onChange={(e) => setBudget(e.target.value)} required aria-label="Monthly budget" data-testid="add-budget-amount" />
      <button type="submit" data-testid="add-budget-submit" className="px-4 py-2 rounded-full bg-[#334E68] hover:bg-[#22384F] text-white text-[14px] font-semibold transition-colors">Add budget</button>
      {error && <p className="sm:col-span-4 text-[13px] text-[#B42318]" role="alert">{error}</p>}
    </form>
  );
}

export default function BudgetPanel({ token }) {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    const r = await fetch(`${API}/api/marketing/stats/budget?month=${month}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null);
    if (!r?.ok) return setError(r?.status === 401 ? "Your session expired. Lock and unlock the page." : "Could not load budgets.");
    setData(await r.json());
  }, [month, token]);

  useEffect(() => { load(); }, [load]);

  const heads = ["Campaign", "Budget", "Spent", "Remaining", "Visits", "Get started", "App Store", "Cost per click"];
  return (
    <section className="bg-white rounded-2xl border border-[#E6E1D8] p-5 sm:p-6 space-y-5" data-testid="budget-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Wallet size={18} className="text-[#E8712C]" />
          <h2 className="font-semibold text-[16px] text-[#1A2A3A]">Monthly ad budget</h2>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setMonth(shiftMonth(month, -1))} aria-label="Previous month" data-testid="budget-prev-month" className="p-2 rounded-full hover:bg-[#F4F6F8]"><ChevronLeft size={16} /></button>
          <span className="min-w-[140px] text-center text-[14px] font-semibold" data-testid="budget-month-label">{monthLabel(month)}</span>
          <button onClick={() => setMonth(shiftMonth(month, 1))} aria-label="Next month" data-testid="budget-next-month" className="p-2 rounded-full hover:bg-[#F4F6F8]"><ChevronRight size={16} /></button>
        </div>
      </div>
      <p className="text-[13px] text-[#5B6B7B] -mt-2">Record what you spend by hand. Nothing here connects to a payment method or spends money.</p>
      <AddBudget month={month} token={token} onSaved={load} />
      {error && <p className="text-[13px] text-[#B42318]" role="alert">{error}</p>}
      <div className="overflow-x-auto -mx-5 sm:mx-0">
        <table className="w-full text-[14px] min-w-[760px]" data-testid="budget-table">
          <thead className="text-[12px] uppercase tracking-wide text-[#5B6B7B] bg-[#FAF7F2]">
            <tr>{heads.map((h, i) => <th key={h} className={`px-4 py-2.5 font-semibold whitespace-nowrap ${i ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {data && data.rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-[#5B6B7B]">No budgets, spending or campaign visits this month yet.</td></tr>
            )}
            {data?.rows.map((r) => <Row key={`${r.source}|${r.campaign}`} r={r} month={month} token={token} onSaved={load} />)}
            {data && data.rows.length > 0 && <Row r={data.total} total />}
          </tbody>
        </table>
      </div>
      {data && <SpendEntries token={token} month={month} rows={data.rows} entries={data.entries} onChange={load} />}
    </section>
  );
}
